import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
  ConditionalOptionValues,
  CreditApplicationFields,
  InternalSignatureDocument,
  InternalSignatureDocumentSignerRole,
  InvitedSignatures,
  isBranchSalesRepAssigned,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getInternalSignatureDocumentForSigning,
  getP21Class1CodesForSigning,
  signInternalSignatureDocumentForSigning,
} from '../../../../redux/services/internalSignatureDocumentService';
import { setCreditApplication } from '../../../../redux/globalSlices/creditApplicationsSlice';
import { getBasicSupplier } from '../../../../redux/services/buyersService';
import { PreviewCreditApplicationData } from '../CreditApplicationComponents/PreviewCreditApplicationData';
import {
  InternalSignatureDocumentFillableFields,
  InternalSignatureDocumentFillableFieldsRef,
} from '../CreditApplicationComponents/InternalSignatureDocumentFillableFields';
import CreateSignatureModal from '../../lienWaivers/supplier/CreateSignatureModal/CreateSignatureModal';
import JobSavingSuccess from '../../../../pages/RequestJobInformationSheet/JobSavingSuccess';
import { decryptCreditApplicationData } from '../../../../utils/decryptCreditApplicationData';
import { useDecryption } from '../../../../utils/useDecryption';
import { DillButton } from '../../../general/Dill';
import PublicFormSidebar from '../CreditApplicationComponents/PublicFormSidebar';
import { isInternalFormFieldVisible } from '../InternalForms/internalFormUtils';
import { openDillToast } from '../../../../utils/helpers';

export const SignInternalSignatureDocument = () => {
  const dispatch = useAppDispatch();
  const [internalSignatureDocumentId, setInternalSignatureDocumentId] = useState('');
  const [role, setRole] = useState<InternalSignatureDocumentSignerRole | ''>('');
  const [email, setEmail] = useState('');
  const [domainId, setDomainId] = useState('');
  const [isSupplierLoading, setIsSupplierLoading] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [internalSignatureDocument, setInternalSignatureDocument] =
    useState<InternalSignatureDocument | null>(null);
  const [fillableFields, setFillableFields] = useState<CreditApplicationFields[]>([]);
  // Fields per row of the document, so it lays out like the template preview.
  const [fillableFieldGroupSizes, setFillableFieldGroupSizes] = useState<number[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [conditionalFieldValues, setConditionalFieldValues] = useState<
    Record<string, ConditionalOptionValues[]>
  >({});
  const fillableFieldsRef = useRef<InternalSignatureDocumentFillableFieldsRef>(null);
  const [isSignatureModalOpen, setSignatureModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [capturedSignature, setCapturedSignature] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [resultDetails, setResultDetails] = useState<{
    modalType: 'Success' | 'Error';
    title: string;
    details: string;
  } | null>(null);
  const [decryptedFieldValues, setDecryptedFieldValues] = useState<any[]>([]);
  const [decryptedBankReference, setDecryptedBankReference] = useState<any[]>([]);
  const [decryptedInvitedSignatures, setDecryptedInvitedSignatures] = useState<InvitedSignatures[]>(
    []
  );
  const [signerLocation, setSignerLocation] = useState('');
  // Live P21 Class 1 codes for the "Marketing Segment" field — see conditionalOptionUtils.ts'
  // resolveFieldOptions/resolveDropdownFieldOptions and getP21Class1CodesForSigning.
  const [class1Options, setClass1Options] = useState<string[]>([]);

  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const { basicSupplier } = useAppSelector((state) => state.buyers);
  const { decryptValue } = useDecryption();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramInternalSignatureDocumentId = params.get('internalSignatureDocumentId') ?? '';
    const paramRole = (params.get('role') ?? '') as InternalSignatureDocumentSignerRole | '';
    const paramEmail = params.get('email') ?? '';
    const paramDomainId = params.get('domainId') ?? '';
    setInternalSignatureDocumentId(paramInternalSignatureDocumentId);
    setRole(paramRole);
    setEmail(paramEmail);
    setDomainId(paramDomainId);

    if (paramDomainId) {
      setIsSupplierLoading(true);
      dispatch(getBasicSupplier({ supplierDomainId: paramDomainId })).finally(() => {
        setIsSupplierLoading(false);
      });
    }

    if (!paramInternalSignatureDocumentId || !paramRole || !paramEmail) {
      setIsLoading(false);
      setResultDetails({
        modalType: 'Error',
        title: 'Invalid Link',
        details: 'This sign-in link is missing information and cannot be used.',
      });
      return;
    }

    (async () => {
      const internalSignatureDocumentResult = await dispatch(
        getInternalSignatureDocumentForSigning({
          internalSignatureDocumentId: paramInternalSignatureDocumentId,
          email: paramEmail,
          role: paramRole,
        })
      );
      setIsLoading(false);
      if (internalSignatureDocumentResult.type.includes('fulfilled')) {
        const payload = (internalSignatureDocumentResult as any).payload;
        // The credit application comes from the signing endpoint, not /getCreditApplication, so
        // it's already trimmed to the document's visibleCreditApplicationFieldIds (Branch/Regional
        // Coversheets only show a few fields) — see restrictCreditApplicationForSigner.
        dispatch(setCreditApplication(payload?.data?.creditApplication ?? null));
        const fetchedInternalSignatureDocument: InternalSignatureDocument | null =
          payload?.data?.internalSignatureDocument ?? null;
        const fetchedFillableFields: CreditApplicationFields[] =
          payload?.data?.fillableFields ?? [];
        setInternalSignatureDocument(fetchedInternalSignatureDocument);
        setFillableFields(fetchedFillableFields);
        setFillableFieldGroupSizes(payload?.data?.fillableFieldGroupSizes ?? []);
        // Only worth the extra P21 round-trip when this document actually has the field.
        if (fetchedFillableFields.some((f) => f.id.includes('marketing-segment'))) {
          dispatch(
            getP21Class1CodesForSigning({
              internalSignatureDocumentId: paramInternalSignatureDocumentId,
            })
          ).then((res) => {
            if (res.type.includes('fulfilled')) {
              const codes: string[] = (res as any).payload?.data?.class1Codes ?? [];
              setClass1Options(codes);
            }
          });
        }
        const valuesById = (fetchedInternalSignatureDocument?.fieldValues ?? []).reduce<
          Record<string, any>
        >((acc, fv) => {
          acc[fv.id] = fv.value;
          return acc;
        }, {});
        setFieldValues(valuesById);
        const conditionalValuesById = (fetchedInternalSignatureDocument?.fieldValues ?? []).reduce<
          Record<string, ConditionalOptionValues[]>
        >((acc, fv) => {
          if (fv.fieldValue && fv.fieldValue.length > 0) acc[fv.id] = fv.fieldValue;
          return acc;
        }, {});
        setConditionalFieldValues(conditionalValuesById);
      } else {
        setResultDetails({
          modalType: 'Error',
          title: 'Unable to Sign',
          details:
            (internalSignatureDocumentResult as any).payload || 'This link is no longer valid.',
        });
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const ipRes = await fetch('https://free.freeipapi.com/api/json/');
        const ipData = await ipRes.json();
        setSignerLocation(`${ipData.cityName}, ${ipData.regionName}, ${ipData.countryName}`);
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const decrypt = async () => {
      if (!creditApplication) return;
      const {
        decryptedFields,
        decryptedBankReferences,
        decryptedInvitedSignatures: freshInvitedSignatures,
      } = await decryptCreditApplicationData(creditApplication, decryptValue);
      if (isMounted) {
        setDecryptedFieldValues(decryptedFields);
        setDecryptedBankReference(decryptedBankReferences);
        setDecryptedInvitedSignatures(freshInvitedSignatures);
      }
    };
    decrypt();
    return () => {
      isMounted = false;
    };
  }, [creditApplication]);

  const signer = useMemo(
    () => internalSignatureDocument?.signers.find((s) => s.role === role) ?? null,
    [internalSignatureDocument, role]
  );

  const previewSigners = useMemo(
    () =>
      (internalSignatureDocument?.signers ?? []).map((s) =>
        s.role === role && capturedSignature
          ? {
              ...s,
              signature: capturedSignature,
              dateSigned: moment(new Date()).format('MM/DD/YYYY hh:mm A'),
            }
          : s
      ),
    [internalSignatureDocument, role, capturedSignature]
  );

  const liveFieldValues = useMemo(
    () =>
      (internalSignatureDocument?.fieldValues ?? []).map((fv) => ({
        ...fv,
        ...(fieldValues[fv.id] !== undefined ? { value: fieldValues[fv.id] } : {}),
        ...(conditionalFieldValues[fv.id] !== undefined
          ? { fieldValue: conditionalFieldValues[fv.id] }
          : {}),
      })),
    [internalSignatureDocument, fieldValues, conditionalFieldValues]
  );

  // Same "supplier-branches"/"supplier-sales-reps" derivation as CreditApplicationRequest.tsx's
  // domainDetails — feeds InternalSignatureDocumentFillableFields' OPTION_FIELD/CHECKBOX_LIST/DROPDOWN
  // fields with those ids (see resolveFieldOptions/resolveDropdownFieldOptions). Only available once
  // basicSupplier has loaded, which requires the link's domainId query param.
  const branchOptions = useMemo(
    () =>
      (basicSupplier?.branches ?? [])
        .filter((branch) => branch?.notInCreditApp !== true)
        .map((branch) => ({ name: branch?.branchName ?? '', erpId: branch?.erpId ?? '' })),
    [basicSupplier]
  );

  // Scoped to the application's own assigned branch when it has one — a rep from some other
  // branch isn't a valid choice here. Falls back to every branch's reps when unassigned, same as
  // before.
  const salesRepOptions = useMemo(() => {
    const seenSalesReps = new Set<string>();
    return (basicSupplier?.branches ?? [])
      .filter((branch) => branch?.notInCreditApp !== true)
      .filter(
        (branch) => !creditApplication?.branchId || branch.branchId === creditApplication.branchId
      )
      .flatMap((branch) => branch?.salesRep ?? [])
      .filter((rep) => {
        const name = rep?.name ?? '';
        if (!name || seenSalesReps.has(name)) return false;
        seenSalesReps.add(name);
        return true;
      })
      .map((rep) => ({
        name: rep?.name ?? '',
        erpId: rep?.erpId ?? '',
        isHouseRep: rep?.isHouseRep ?? false,
      }));
  }, [basicSupplier, creditApplication?.branchId]);

  const selectedBranch = useMemo(
    () =>
      basicSupplier?.branches?.find((branch) => branch.branchId === creditApplication?.branchId),
    [basicSupplier, creditApplication?.branchId]
  );

  const firstSelectedSalesRep = useMemo(() => {
    const assignedSalesRep = creditApplication?.assignedSalesRep;
    if (!assignedSalesRep?.length) return undefined;
    const salesReps = basicSupplier?.branches?.flatMap((branch) => branch?.salesRep ?? []) ?? [];
    return salesReps.find((salesRep) => isBranchSalesRepAssigned(salesRep, assignedSalesRep));
  }, [creditApplication?.assignedSalesRep, basicSupplier]);

  const selectedRegion = useMemo(() => {
    if (!selectedBranch?.branchId) return undefined;
    return basicSupplier?.regions?.find((region) =>
      (region.branchIds ?? []).includes(selectedBranch.branchId)
    );
  }, [selectedBranch, basicSupplier]);

  const internalSignatureDocumentExtraValues = useMemo(
    () => ({
      branchName: selectedBranch?.branchName ?? '',
      branchErpId: selectedBranch?.erpId ?? '',
      salesRepName: firstSelectedSalesRep?.name ?? '',
      salesRepErpId: firstSelectedSalesRep?.erpId ?? '',
      territoryName: selectedRegion?.regionName ?? '',
    }),
    [selectedBranch, firstSelectedSalesRep, selectedRegion]
  );

  // Fields with "Show this field only if…" hide until their source field (on this coversheet) has
  // one of the chosen answers. Hidden fields aren't required and are saved blank.
  const visibleFillableFields = useMemo(() => {
    const fieldsById = Object.fromEntries(fillableFields.map((field) => [field.id, field]));
    return fillableFields.filter((field) =>
      isInternalFormFieldVisible(field, fieldValues, fieldsById)
    );
  }, [fillableFields, fieldValues]);

  // The row sizes again, counting only the fields still showing in each row.
  const visibleFillableFieldGroupSizes = useMemo(() => {
    const visibleIds = new Set(visibleFillableFields.map((field) => field.id));
    let cursor = 0;
    return fillableFieldGroupSizes.map((size) => {
      const row = fillableFields.slice(cursor, cursor + size);
      cursor += size;
      return row.filter((field) => visibleIds.has(field.id)).length;
    });
  }, [fillableFields, fillableFieldGroupSizes, visibleFillableFields]);

  const isFieldValueMissing = (field: CreditApplicationFields, value: any) => {
    if (!field.isRequired) return false;
    if (field.fromField === 'CHECK_BOX') return value !== true;
    return value === null || value === undefined || value === '';
  };

  const missingRequiredFields = useMemo(
    () =>
      visibleFillableFields.filter((field) => isFieldValueMissing(field, fieldValues[field.id])),
    [visibleFillableFields, fieldValues]
  );

  const fieldErrors = useMemo(() => {
    if (!submitAttempted) return {};
    return missingRequiredFields.reduce<Record<string, string>>((acc, field) => {
      acc[field.id] = 'Required';
      return acc;
    }, {});
  }, [submitAttempted, missingRequiredFields]);

  const handleSignNowClick = () => {
    setSubmitAttempted(true);
    if (missingRequiredFields.length > 0) {
      openDillToast({
        type: 'ERROR',
        message: `Please fill in required field${
          missingRequiredFields.length > 1 ? 's' : ''
        }: ${missingRequiredFields.map((field) => field.name || field.id).join(', ')}`,
      });
      return;
    }

    if (fillableFieldsRef.current && !fillableFieldsRef.current.validate()) {
      openDillToast({ type: 'ERROR', message: 'Please complete the highlighted fields below.' });
      return;
    }
    setSignatureModalOpen(true);
  };

  const handleSignatureCaptured = (signature: string) => {
    if (!signature) return;
    setCapturedSignature(signature);
    setSignatureModalOpen(false);
  };

  const handleClearSignature = () => {
    setCapturedSignature('');
  };

  const handleSubmitSignature = async () => {
    if (!capturedSignature || isSubmitting || !internalSignatureDocumentId || !role) return;
    setIsSubmitting(true);
    const result = await dispatch(
      signInternalSignatureDocumentForSigning({
        internalSignatureDocumentId,
        email,
        role,
        signature: capturedSignature,
        location: signerLocation,
        fieldValues: fillableFields.map((field) => {
          const isVisible = visibleFillableFields.includes(field);
          return {
            id: field.id,
            value: isVisible ? fieldValues[field.id] ?? null : null,
            ...(isVisible && conditionalFieldValues[field.id]
              ? { fieldValue: conditionalFieldValues[field.id] }
              : {}),
          };
        }),
      })
    );
    setIsSubmitting(false);
    if (result.type.includes('fulfilled')) {
      setResultDetails({
        modalType: 'Success',
        title: 'Document Signed',
        details: 'Thank you — your signature has been recorded.',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to sign the internal signature document.',
      });
    }
  };

  const title = internalSignatureDocument?.documentName || 'Internal Signature Document';

  return (
    <div className="w-full GREY_100-BG min-h-full flex flex-col md:flex-row">
      <CreateSignatureModal
        open={isSignatureModalOpen}
        handleClose={() => setSignatureModalOpen(false)}
        addSignature={(signature) => handleSignatureCaptured(signature)}
        onDownloadRealease={() => {}}
        defaultIsManualSign={false}
        title={`Sign ${title}`}
        subText={`Confirm your name & signature to sign off on this ${title.toLowerCase()}${
          signer?.roleLabel ? ` as ${signer.roleLabel}` : ''
        }.`}
      />

      {domainId && <PublicFormSidebar supplier={basicSupplier} isLoading={isSupplierLoading} />}
      <div className="flex flex-col flex-1 min-w-0 pb-2 md:pb-4">
        <div className="w-full flex items-center justify-center text-lg md:text-xl WHITE-BG font-bold p-2 md:p-5">
          {`Sign ${title}`}
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center w-full min-h-[60vh]">
            <p className="text-sm GREY_500-CLR">Loading…</p>
          </div>
        ) : resultDetails ? (
          <div className="flex flex-col items-center w-full justify-center">
            <div className="w-[calc(100%-30px)] md:w-[512px]">
              <JobSavingSuccess
                type={resultDetails.modalType}
                details={resultDetails.details}
                title={resultDetails.title}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col px-2 md:px-5 py-2 md:py-4 w-full justify-center items-center">
            <div className="w-full md:w-2/3 flex flex-col gap-4">
              {creditApplication ? (
                <div className="bg-white p-2 md:p-4">
                  <PreviewCreditApplicationData
                    formData={decryptedFieldValues}
                    bankReference={decryptedBankReference}
                    inviteSignatures={decryptedInvitedSignatures}
                    isOnlyView
                  />
                </div>
              ) : null}
              <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col gap-3">
                <span className="text-sm font-bold text-gray-800 tracking-wide">{title}</span>
                {visibleFillableFields.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-gray-700">
                      Please fill in the details below
                    </span>
                    <InternalSignatureDocumentFillableFields
                      ref={fillableFieldsRef}
                      fields={visibleFillableFields}
                      groupSizes={visibleFillableFieldGroupSizes}
                      values={fieldValues}
                      fieldValues={conditionalFieldValues}
                      onChange={(fieldId, value, fieldValue) => {
                        setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
                        if (fieldValue !== undefined) {
                          setConditionalFieldValues((prev) => ({ ...prev, [fieldId]: fieldValue }));
                        }
                      }}
                      errors={fieldErrors}
                      branches={branchOptions}
                      salesReps={salesRepOptions}
                      class1Options={class1Options}
                      currencyFormatting
                    />
                  </div>
                ) : null}
                <div className="flex flex-col gap-3 border-t border-gray-100 pt-3">
                  <div className="flex flex-col border border-gray-200 rounded-xl overflow-hidden">
                    <div className="flex justify-end gap-2 px-2 pt-2">
                      {capturedSignature && (
                        <DillButton
                          label="Clear"
                          variant="secondary"
                          height={28}
                          isDisabled={isSubmitting}
                          onClick={handleClearSignature}
                        />
                      )}
                      <DillButton
                        label={capturedSignature ? 'Change Signature' : 'Sign Now'}
                        variant={capturedSignature ? 'secondary' : 'primary'}
                        height={28}
                        isDisabled={isSubmitting}
                        onClick={handleSignNowClick}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 p-1">
                      <div className="flex flex-col items-start gap-1 p-2">
                        <span className="text-xs font-semibold text-gray-700">
                          {signer?.roleLabel || signer?.role}
                        </span>
                        <span className="text-xs text-gray-500">{email}</span>
                      </div>
                      <div className="flex flex-col items-start gap-1 p-2">
                        {(capturedSignature || signer?.signature) && (
                          <div className="flex items-center gap-2">
                            <img
                              src={capturedSignature || signer?.signature}
                              alt="Signature preview"
                              className="h-6 w-auto object-contain"
                            />
                            <p className="text-[9px] text-gray-400">E-signed using Dill</p>
                          </div>
                        )}
                        {capturedSignature && (
                          <div className="text-[10px] text-gray-500">
                            {`Date Signed: ${moment(new Date()).format('MM/DD/YYYY hh:mm A')}`}
                            {signerLocation && ` at ${signerLocation}`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center border-t border-gray-100 pt-3">
                    <DillButton
                      label="Submit"
                      variant="primary"
                      centered
                      isDisabled={!capturedSignature || isSubmitting}
                      isLoading={isSubmitting}
                      onClick={handleSubmitSignature}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignInternalSignatureDocument;
